/** Maps a wizard user's (onboard_path, department_key, designation_key)
 *  to one of the seven crew dashboard archetypes.
 *
 *  Priority order:
 *    1. Explicit designation override (e.g. "director" → filmmaker even
 *       though other Direction-dept sub-roles fall to craft).
 *    2. Department-level default from PATH_DEPT_MAP.
 *    3. If the user has no wizard fields (legacy signup) → null,
 *       which tells the router to fall through to the legacy generic
 *       <Dashboard/> so PH-owners, investors and admins keep their
 *       existing landing pages.
 */
export const ARCHETYPE_KEYS = [
  "filmmaker", "craft", "talent", "labor",
  "agency_staff", "office_staff", "creator_support",
];

// Certain designations override their department's default archetype.
const DESIGNATION_OVERRIDE = {
  // Direction dept — only the DIRECTOR gets the Filmmaker cockpit; every
  // Assistant / Associate / 2nd AD / Trainee falls to Craft (they DO
  // the work but don't approve the money).
  director:            "filmmaker",
  associate_director:  "craft",
  assistant_director:  "craft",
  second_ad:           "craft",
  trainee_ad:          "craft",
};

// Path × department → archetype.
const PATH_DEPT_MAP = {
  creative: {
    direction:                  "craft",          // overridden by DESIGNATION_OVERRIDE for "director"
    production_office_creative: "filmmaker",       // freelance LP / EP / PM = filmmaker
    camera:                     "craft",
    sound:                      "craft",
    music:                      "craft",
    screenwriting:              "craft",
    editing:                    "craft",
    vfx_post:                   "craft",
    art_design:                 "craft",
    costume:                    "craft",
    makeup_hair:                "craft",
    casting:                    "craft",
    stunt:                      "craft",
    choreography:               "craft",
    performing_artist:          "talent",
    still_photo:                "craft",
    grip_lighting:              "labor",
    spot_support:               "labor",
    sfx:                        "labor",
    content_creator:            "creator_support",
    agency_creative:            "agency_staff",
  },
  managerial: {
    production_office_mgr:      "filmmaker",       // in-house production office = filmmaker widgets
    accounts_finance:           "office_staff",
    hr_admin:                   "office_staff",
    legal_compliance:           "office_staff",
    agency_servicing:           "agency_staff",
    agency_media:               "agency_staff",
    marketing_community:        "agency_staff",
    vendor_ops:                 "office_staff",
    support_staff:              "creator_support",
    trainee:                    "creator_support",
  },
};

export function pickArchetype(user) {
  if (!user) return null;
  // 1. Designation-level override — beats everything.
  if (user.designation_key && DESIGNATION_OVERRIDE[user.designation_key]) {
    return DESIGNATION_OVERRIDE[user.designation_key];
  }
  // 2. Department-level default.
  if (user.onboard_path && user.department_key) {
    return PATH_DEPT_MAP[user.onboard_path]?.[user.department_key] || null;
  }
  return null;
}
